import classnames from 'classnames';
import { format, getMonth, getYear, startOfYear } from 'date-fns';
import React, { useEffect, useState } from 'react';

import { MonthDropdownOptions } from './MonthDropdownOptions';
import { Month } from './types';
import { useAuth } from '../../app/AuthorizationProvider';
import { getNameOfMonthWithOffset } from '../../utils/date';

interface MonthDropdownProps {
  onChange: (month: number) => void;
  date: Date;
  minDate?: Date;
  maxDate?: Date;
}

export const getMonthsList = (date: Date, minDate?: Date, maxDate?: Date, localeObj?: Locale): Month[] => {
  const monthArray = Array.from({ length: 12 });
  const startOfYearDate = startOfYear(date);

  if (!minDate && !maxDate) {
    return monthArray.map((_, index) => ({
      disabled: false,
      name: getNameOfMonthWithOffset(startOfYearDate, index, localeObj),
    }));
  }

  let minIndex = 0;
  let maxIndex = 11;

  if (minDate && getYear(date) === getYear(minDate)) {
    minIndex = getMonth(minDate);
  }

  if (maxDate && getYear(date) === getYear(maxDate)) {
    maxIndex = getMonth(maxDate);
  }

  return monthArray.map((_, index) => ({
    disabled: index < minIndex || index > maxIndex,
    name: getNameOfMonthWithOffset(startOfYearDate, index, localeObj),
  }));
};

export const MonthDropdown: React.FC<MonthDropdownProps> = ({ date, onChange, minDate, maxDate }) => {
  const { localeObj } = useAuth();
  const [monthsList, setMonthsList] = useState(getMonthsList(date, minDate, maxDate, localeObj));
  const [dropdownVisible, setDropdownVisible] = useState(false);

  useEffect(() => {
    setMonthsList(getMonthsList(date, minDate, maxDate, localeObj));
  }, [date, minDate, maxDate, localeObj]);

  const onMonthChange = (month: number) => {
    onChange(month);
    setDropdownVisible(false);
  };

  const renderReadView = () => (
    <div
      className={classnames('react-datepicker__month-read-view', {
        'react-datepicker__month-read-view--hidden': dropdownVisible,
      })}
      onClick={() => setDropdownVisible(true)}
    >
      <span className="react-datepicker__month-read-view--selected-month">
        {format(date, 'LLLL', { locale: localeObj })}
      </span>
      <span className="react-datepicker__month-read-view--down-arrow" />
    </div>
  );

  return (
    <div className="react-datepicker__month-dropdown-container react-datepicker__month-dropdown-container--scroll">
      {dropdownVisible && (
        <MonthDropdownOptions
          currentMonthIndex={getMonth(date)}
          months={monthsList}
          onChange={onMonthChange}
          onCancel={() => setDropdownVisible(false)}
        />
      )}
      {renderReadView()}
    </div>
  );
};
